// Packages
import dayjs from "dayjs";

// Const
import { rssiDefinition } from '../const';

// Helper
import { formatTimestamp, formatDuration } from "utils/format";

export const getDeviceRegisterSendRequestParams = ({ auth }) => {
  const isStandaloneMode = !auth.orgs || Object.keys(auth.orgs).length === 0;
  const apiBaseUrl = isStandaloneMode ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;
  const apiPaths = {
    site: `${apiBaseUrl}/sites/:siteId/devices/regeneration.update`,
  };
  const apiKey = apiPaths[auth.scopeLevel];

  const restfulParamsConfig = {
    standalone: {
      orgId: auth.realm?.id,
      siteId: auth.sites?.id,
    },
    nonStandalone: {
      entId: auth.realm?.id,
      orgId: auth.orgs?.id,
      siteId: auth.sites?.id,
    },
  };

  const restfulParams = isStandaloneMode ? restfulParamsConfig["standalone"] : restfulParamsConfig["nonStandalone"];

  return {
    apiKey,
    restfulParams
  };
}

export const getMainSendRequestParams = ({ auth }) => {
  const isStandaloneMode = !auth.orgs || Object.keys(auth.orgs).length === 0;
  const apiBaseUrl = isStandaloneMode ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;
  const apiPaths = {
    site: `${apiBaseUrl}/sites/:siteId/clients.read`,
  };
  const apiKey = apiPaths[auth.scopeLevel];

  const restfulParamsConfig = {
    standalone: {
      orgId: auth.realm?.id,
      siteId: auth.sites?.id,
    },
    nonStandalone: {
      entId: auth.realm?.id,
      orgId: auth.orgs?.id,
      siteId: auth.sites?.id,
    },
  };

  const restfulParams = isStandaloneMode ? restfulParamsConfig["standalone"] : restfulParamsConfig["nonStandalone"];

  return {
    apiKey,
    restfulParams
  };
}

const getClientName = ({
  client
}) => {
  const { clientName, clientId } = client;
  const name = clientName || clientId || null; // Use mac address instead if the client name is empty.
  return name
}

const getUpTime = ({
  status,
  lastSeen,
}) => {
  let upTime = null;
  const tmpStatus = status ? status.toLowerCase() : null;

  if (tmpStatus === "online" && lastSeen) {
    const nowUnix = dayjs().unix();
    const lastSeenUnix = +lastSeen > 10000000000 ? Math.floor(+lastSeen / 1000) : +lastSeen;

    upTime = formatDuration(nowUnix - lastSeenUnix);
  }

  return upTime;
}

const getLastSeen = ({
  status,
  lastSeen,
}) => {
  let formattedLastSeen = null;

  if (status === "online") {
    formattedLastSeen = "Online";
  } else if (status === "offline" && lastSeen) {
    formattedLastSeen = formatTimestamp(lastSeen);
  }

  return formattedLastSeen;
};

const getUsage = ({ bytes, decimals = 2 }) => {
  const numberedBytes = +bytes;
  if (numberedBytes === 0) return "0 Bytes";

  const k = 1024;  // base unit
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(numberedBytes) / Math.log(k));
  const value = numberedBytes / Math.pow(k, i);

  return value.toFixed(decimals) + " " + sizes[i];
}

export const getRssiStrength = ({
  isWiredClient,
  rssi
}) => {
  let strength = null;

  if (isWiredClient) return strength;

  if (+rssi >= -70) strength = rssiDefinition.wireless.excellent.value;
  else if (+rssi < -70 && +rssi >= -75) strength = rssiDefinition.wireless.good.value;
  else if (+rssi < -75) strength = rssiDefinition.wireless.poor.value;
  else strength = rssiDefinition.wireless.poor.value;

  return strength;
}

export const getRssiIcon = ({
  client
}) => {
  const { ssid, rssi } = client;
  const isWiredClient = !ssid;
  const strength = getRssiStrength({ isWiredClient, rssi });
  const rssiIcon = isWiredClient ? rssiDefinition.wired.icon : rssiDefinition.wireless[strength].icon;

  return rssiIcon;
}

export const initTableData = ({
  responseData
}) => {
  return responseData.map(data => {

    const {
      status,
      lastSeen,
      upload,
      download,
      traffic24HR,
      band,
      rssi,
      ssid,
      interface: interfaceVal
    } = data;

    const formattedStatus = String(status) ? String(status).toLowerCase() : null;

    const formattedName = getClientName({ client: data });
    const formattedUpTime = getUpTime({ lastSeen, status: formattedStatus });
    const formattedLastSeen = getLastSeen({ lastSeen, status: formattedStatus });
    const formattedTraffic = traffic24HR ? getUsage({ bytes: traffic24HR }) : null;
    const formattedUpload = upload ? getUsage({ bytes: upload }) : null;
    const formattedDownload = download ? getUsage({ bytes: download }) : null;
    const formattedSignal = band && rssi ? `${band}/${rssi} %` : null;

    const isWirelessType = !!band || !!ssid;
    const hasInterface = interfaceVal !== null && interfaceVal !== undefined && interfaceVal !== "";
    const formattedInterface = hasInterface
      ? (isWirelessType ? String(interfaceVal) : `Ethernet - Port ${interfaceVal}`)
      : null;

    return {
      ...data,
      status: formattedStatus,
      formattedName,
      formattedUpTime,
      formattedLastSeen,
      formattedTraffic,
      formattedUpload,
      formattedDownload,
      formattedSignal,
      formattedInterface
    }
  })
}